/**
 * Shared constants + formatting helpers for the seller booking admin system.
 * Shared by every booking subcomponent, whether it is mounted inside
 * BookingManager or standalone. Calendar wall-time conversion uses Luxon so
 * seller-authored exceptions remain DST-correct.
 * Mirrors the backend enums in models/* and server/helpers/booking/config.js.
 *
 * The date/money formatters themselves live in `helpers/bookingFormat.js` and
 * are re-exported here — the shopper-facing slot picker renders the same
 * intervals and must not format them differently.
 */
import { DateTime } from 'luxon';

export {
    formatDateTime,
    formatDateOnly,
    formatTimeOnly,
    formatInterval,
    formatCents,
    formatDurationMinutes,
    guessTimezone,
    dateKeyInZone,
    resolveBookingFormatLocale,
} from '../../../helpers/bookingFormat';

/** Render an instant as a `datetime-local` value in a calendar's IANA zone. */
export function toCalendarDateTimeInput(value, timezone) {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    const zoned = DateTime.fromJSDate(date, { zone: timezone || undefined });
    return zoned.isValid ? zoned.toFormat("yyyy-MM-dd'T'HH:mm") : '';
}

/**
 * Interpret a `datetime-local` wall time in a calendar's IANA zone and return
 * its UTC ISO instant. Reject DST-gap values that Luxon would otherwise shift.
 */
export function calendarDateTimeInputToIso(value, timezone) {
    if (!value || !timezone) return null;
    const zoned = DateTime.fromFormat(value, "yyyy-MM-dd'T'HH:mm", {
        zone: timezone,
        setZone: true,
    });
    if (!zoned.isValid || zoned.toFormat("yyyy-MM-dd'T'HH:mm") !== value) return null;
    return zoned.toUTC().toISO();
}

export const RESOURCE_TYPES = ['staff', 'space', 'asset', 'pickup', 'seller'];

export const RESOURCE_TYPE_LABELS = {
    staff: 'Staff member',
    space: 'Space / room',
    asset: 'Asset / unit',
    pickup: 'Pickup counter',
    seller: 'General calendar',
};

export const EXCEPTION_KINDS = ['blackout', 'open', 'override'];

export const EXCEPTION_KIND_LABELS = {
    blackout: 'Blackout',
    open: 'Extra opening',
    override: 'Override',
};

export const BOOKING_STATUSES = [
    'hold', 'pending', 'confirmed', 'completed', 'canceled', 'no_show',
];

export const BOOKING_STATUS_LABELS = {
    hold: 'Hold',
    pending: 'Pending approval',
    confirmed: 'Confirmed',
    completed: 'Completed',
    canceled: 'Canceled',
    no_show: 'No-show',
};

// Class names map to color families defined in BookingManager.css.
export const BOOKING_STATUS_TONE = {
    hold: 'muted',
    pending: 'warn',
    confirmed: 'ok',
    completed: 'info',
    canceled: 'bad',
    no_show: 'bad',
};

export const BOOKING_MODE_LABELS = {
    appointment: 'Appointment',
    daterange: 'Date range',
    pickup: 'Pickup',
    custom: 'Custom',
};

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const DAY_LABELS = {
    mon: 'Monday',
    tue: 'Tuesday',
    wed: 'Wednesday',
    thu: 'Thursday',
    fri: 'Friday',
    sat: 'Saturday',
    sun: 'Sunday',
};

export const DAY_SHORT = {
    mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

/**
 * A short label for a booking's customer — guest name/email, or a fallback.
 */
export function bookingCustomerLabel(booking) {
    if (!booking) return 'Customer';
    if (booking.guestName) return booking.guestName;
    if (booking.guestEmail) return booking.guestEmail;
    if (booking.metadata && booking.metadata.customerName) return booking.metadata.customerName;
    return booking.orderId ? `Order #${booking.orderId}` : 'Customer';
}
